import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import TableCell from "@mui/material/TableCell"
import Typography from "@mui/material/Typography"
import { format } from "date-fns"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
} from "../components/AdminHeaderTableCell"
import AdminLiquidSurface from "../components/AdminLiquidSurface"
import AdminListToolbar from "../components/AdminListToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import { fetchAllStaffPages } from "../hooks/fetchAllStaffPages"
import {
  formatAdminLoadError,
  useAdminPaginatedQuery,
} from "../hooks/useAdminPaginatedQuery"
import ActivityReportStatusChip from "./ActivityReportStatusChip"
import ActivityReportStatusFilter from "./ActivityReportStatusFilter"
import AdminReportDateRangePicker from "./AdminReportDateRangePicker"
import ViewActivityReportDialog from "./ViewActivityReportDialog"
import {
  REPORT_STATUS,
  filterActivityReportRows,
  mergeActivityReportRows,
  pendingItemToRow,
  reportRowKey,
} from "./activityReportRowUtils"
import { formatReportRangeLabel } from "./reportDateRangePresets"

// All / Pending merge logs with the analytics pending list in the browser, so
// they load every log page for the range. Past this, narrow the range.
export const ACTIVITY_LOG_CLIENT_MAX_ROWS = 2000

export const ACTIVITY_LOG_FILTER_SPECS = {
  status: {
    default: REPORT_STATUS.all,
    toParams: (value) =>
      value === REPORT_STATUS.sent || value === REPORT_STATUS.failed
        ? { status: value }
        : {},
  },
  concierge: { param: "concierge_user_id" },
}

const TOOLBAR_FILTERS = [
  {
    key: "concierge",
    label: "Concierge",
    type: "entity",
    entity: "concierge",
    placeholder: "All concierges",
  },
]

function rowBackgroundColor(status) {
  if (status === REPORT_STATUS.pending) {
    return "colors.gray100"
  }
  if (status === REPORT_STATUS.failed) {
    return "rgba(211, 47, 47, 0.04)"
  }
  return undefined
}

export default function ActivityReportLogsTable({
  dateRange,
  onDateRangeChange,
  analytics,
  analyticsLoading = false,
  onRefresh,
}) {
  const [sendingKey, setSendingKey] = useState("")
  const [viewContext, setViewContext] = useState(null)
  const [clientPageIndex, setClientPageIndex] = useState(1)
  const [clientPageSize, setClientPageSize] = useState(20)
  const [allLogs, setAllLogs] = useState({
    rows: [],
    total: 0,
    truncated: false,
  })
  const [allLogsLoading, setAllLogsLoading] = useState(false)
  const [allLogsError, setAllLogsError] = useState(null)
  const [allLogsToken, setAllLogsToken] = useState(0)

  const rangeParams = useMemo(
    () => ({
      report_date_from: dateRange?.from || undefined,
      report_date_to: dateRange?.to || undefined,
    }),
    [dateRange?.from, dateRange?.to]
  )

  // Read before the hook so the server-paged query only runs for Sent/Failed.
  const [searchParams] = useSearchParams()
  const statusFilter = searchParams.get("status") || REPORT_STATUS.all
  const useClientRows =
    statusFilter === REPORT_STATUS.all || statusFilter === REPORT_STATUS.pending

  const query = useAdminPaginatedQuery(adminApi.fetchActivityReportLogs, {
    filters: ACTIVITY_LOG_FILTER_SPECS,
    extraParams: rangeParams,
    defaultSortColumn: "sent_at",
    defaultSortDirection: "desc",
    enabled: !useClientRows,
  })
  const conciergeUserId = query.filters.concierge || null

  const {
    rows: logRows,
    isLoading: logsLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    searchText,
    refresh,
  } = query

  // Client-merged modes: every log page for the range (not one capped page).
  useEffect(() => {
    if (statusFilter !== REPORT_STATUS.all) {
      return undefined
    }
    let cancelled = false
    setAllLogsLoading(true)
    setAllLogsError(null)
    fetchAllStaffPages(
      adminApi.fetchActivityReportLogs,
      {
        ...rangeParams,
        concierge_user_id: conciergeUserId || undefined,
        ordering: "-sent_at",
      },
      { maxRows: ACTIVITY_LOG_CLIENT_MAX_ROWS }
    )
      .then((result) => {
        if (!cancelled) setAllLogs(result)
      })
      .catch((error) => {
        if (cancelled) return
        setAllLogs({ rows: [], total: 0, truncated: false })
        setAllLogsError(formatAdminLoadError(error))
      })
      .finally(() => {
        if (!cancelled) setAllLogsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [statusFilter, rangeParams, conciergeUserId, allLogsToken])

  useEffect(() => {
    setClientPageIndex(1)
  }, [statusFilter, conciergeUserId, searchText, rangeParams])

  const mergedRows = useMemo(() => {
    if (statusFilter === REPORT_STATUS.pending) {
      return (analytics?.pending || []).map(pendingItemToRow)
    }
    if (statusFilter === REPORT_STATUS.all) {
      return mergeActivityReportRows(analytics?.pending || [], allLogs.rows)
    }
    return logRows.map((row) => ({
      ...row,
      status: row.status,
    }))
  }, [analytics?.pending, allLogs.rows, logRows, statusFilter])

  const filteredRows = useMemo(() => {
    if (!useClientRows) {
      return mergedRows
    }
    return filterActivityReportRows(mergedRows, {
      searchText,
      conciergeUserId,
      statusFilter:
        statusFilter === REPORT_STATUS.all ? REPORT_STATUS.all : statusFilter,
    })
  }, [mergedRows, searchText, conciergeUserId, statusFilter, useClientRows])

  const displayRows = useMemo(() => {
    if (!useClientRows) {
      return filteredRows
    }
    const start = (clientPageIndex - 1) * clientPageSize
    return filteredRows.slice(start, start + clientPageSize)
  }, [filteredRows, useClientRows, clientPageIndex, clientPageSize])

  const displayTotal = useClientRows ? filteredRows.length : totalResultsSize
  const displayPageIndex = useClientRows ? clientPageIndex : pageIndex
  const displayPageSize = useClientRows ? clientPageSize : pageSize
  const setDisplayPageIndex = useClientRows ? setClientPageIndex : setPageIndex
  const setDisplayPageSize = useClientRows ? setClientPageSize : setPageSize
  const tableLoading =
    analyticsLoading ||
    (statusFilter === REPORT_STATUS.all && allLogsLoading) ||
    (!useClientRows && logsLoading)
  // The toolbar shows the server-mode error; client modes have their own.
  const toolbarQuery = useClientRows
    ? {
        ...query,
        loadError: allLogsError,
        refresh: () => setAllLogsToken((t) => t + 1),
      }
    : query

  const handleManualSendComplete = useCallback(() => {
    onRefresh?.()
    refresh()
    setAllLogsToken((token) => token + 1)
  }, [onRefresh, refresh])

  async function handleSend(row) {
    const key = reportRowKey(row)
    setSendingKey(key)
    try {
      await adminApi.sendReportManually({
        concierge_user_id: row.concierge_user_id,
        account_owner_id: row.account_owner_id,
        report_date: row.report_date,
      })
      handleManualSendComplete()
    } finally {
      setSendingKey("")
    }
  }

  function handleStatusFilterChange(nextFilter) {
    query.setFilter("status", nextFilter)
  }

  function handleDateRangeChange(nextRange) {
    onDateRangeChange?.(nextRange)
  }

  return (
    <Column gap={2}>
      <AdminLiquidSurface variant="card" sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Column gap={2}>
          <Row
            sx={{
              alignItems: { xs: "stretch", lg: "flex-start" },
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Column gap={0.25} sx={{ flex: 1, minWidth: 240 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Activity reports
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatReportRangeLabel(dateRange, analytics?.report_timezone)}
              </Typography>
            </Column>
            <AdminReportDateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              helperText="One date range drives summary counts and the table below."
            />
          </Row>

          <ActivityReportStatusFilter
            value={statusFilter}
            onChange={handleStatusFilterChange}
            analytics={analytics}
            loading={analyticsLoading}
          />

          <AdminListToolbar
            query={toolbarQuery}
            filters={TOOLBAR_FILTERS}
            showCount={false}
          />
          {statusFilter === REPORT_STATUS.all && allLogs.truncated ? (
            <Alert severity="warning">
              Showing the first {ACTIVITY_LOG_CLIENT_MAX_ROWS.toLocaleString()}{" "}
              of {allLogs.total.toLocaleString()} sent and failed reports.
              Narrow the date range or pick a concierge to see the rest.
            </Alert>
          ) : null}

          <AdminListingTable
            rows={displayRows}
            isLoading={tableLoading}
            pageIndex={displayPageIndex}
            setPageIndex={setDisplayPageIndex}
            pageSize={displayPageSize}
            setPageSize={setDisplayPageSize}
            totalResultsSize={displayTotal}
            columnCount={8}
            tableHead={
              <AdminTableHeadRow>
                {useClientRows ? (
                  <AdminHeaderTableCell minWidth={{ xs: 72, sm: 88 }}>
                    Report date
                  </AdminHeaderTableCell>
                ) : (
                  <AdminSortableHeaderCell
                    columnName="report_date"
                    label="Report date"
                    query={query}
                    minWidth={{ xs: 72, sm: 88 }}
                  />
                )}
                <AdminHeaderTableCell minWidth={{ xs: 88, sm: 112 }}>
                  Concierge
                </AdminHeaderTableCell>
                <AdminHeaderTableCell minWidth={{ xs: 96, sm: 120 }}>
                  Parent account
                </AdminHeaderTableCell>
                <AdminHeaderTableCell minWidth={{ xs: 56, sm: 72 }}>
                  Status
                </AdminHeaderTableCell>
                {useClientRows ? (
                  <AdminHeaderTableCell minWidth={{ xs: 72, sm: 88 }}>
                    Sent at
                  </AdminHeaderTableCell>
                ) : (
                  <AdminSortableHeaderCell
                    columnName="sent_at"
                    label="Sent at"
                    query={query}
                    minWidth={{ xs: 72, sm: 88 }}
                  />
                )}
                <AdminHeaderTableCell minWidth={{ xs: 88, sm: 120 }}>
                  To
                </AdminHeaderTableCell>
                <AdminHeaderTableCell minWidth={{ xs: 120, sm: 160 }}>
                  Notes
                </AdminHeaderTableCell>
                <AdminHeaderTableCell minWidth={{ xs: 120, sm: 160 }}>
                  Actions
                </AdminHeaderTableCell>
              </AdminTableHeadRow>
            }
          >
            {displayRows.map((row) => {
              const key = row.id || reportRowKey(row)
              const canSend =
                row.status === REPORT_STATUS.pending ||
                row.status === REPORT_STATUS.failed
              return (
                <AdminTableRow
                  key={key}
                  hover
                  sx={{ backgroundColor: rowBackgroundColor(row.status) }}
                >
                  <AdminTruncatedCell value={row.report_date || "—"} />
                  <AdminTruncatedCell
                    value={row.concierge_name || row.concierge_email}
                  />
                  <AdminTruncatedCell
                    value={
                      row.account_owner_name || row.account_owner_email || "—"
                    }
                  />
                  <TableCell>
                    <ActivityReportStatusChip status={row.status} />
                  </TableCell>
                  <AdminTruncatedCell
                    value={
                      row.sent_at
                        ? format(new Date(row.sent_at), "MMM d, yyyy h:mm a")
                        : "—"
                    }
                  />
                  <AdminTruncatedCell value={row.to_email || "—"} />
                  <AdminTruncatedCell
                    value={
                      row.notes || (row.has_notes ? "Saved (open View)" : "—")
                    }
                  />
                  <TableCell align="right">
                    {row.concierge_user_id && row.account_owner_id ? (
                      <Row gap={1} sx={{ justifyContent: "flex-end" }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setViewContext(row)}
                        >
                          View
                        </Button>
                        {canSend ? (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={sendingKey === reportRowKey(row)}
                            onClick={() => handleSend(row)}
                          >
                            {sendingKey === reportRowKey(row) ? (
                              <CircularProgress size={16} />
                            ) : row.status === REPORT_STATUS.failed ? (
                              "Retry"
                            ) : (
                              "Send"
                            )}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={sendingKey === reportRowKey(row)}
                            onClick={() => handleSend(row)}
                          >
                            {sendingKey === reportRowKey(row) ? (
                              <CircularProgress size={16} />
                            ) : (
                              "Resend"
                            )}
                          </Button>
                        )}
                      </Row>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </AdminTableRow>
              )
            })}
          </AdminListingTable>
        </Column>
      </AdminLiquidSurface>
      <ViewActivityReportDialog
        open={Boolean(viewContext)}
        context={viewContext}
        onClose={() => setViewContext(null)}
      />
    </Column>
  )
}
